import {
  ChangeEvent,
  CSSProperties,
  FormEvent,
  KeyboardEvent,
  useEffect,
  useRef,
} from 'react';
import { Box, SystemStyleObject, Textarea } from '@chakra-ui/react';
import { Button, Colors, Icon } from '@mercator-gcp/grout';

const wrapperStyle = {
  position: 'relative' as const,
  borderWidth: '2px',
  borderStyle: 'solid',
  borderColor: 'gray.200',
  borderRadius: '24px',
  backgroundColor: Colors.ArmyGreen200,
  width: '100%',
};

const MIN_HEIGHT = 45;
const MAX_HEIGHT = 400;

const textareaStyle: CSSProperties = {
  width: '100%',
  minHeight: `${MIN_HEIGHT}px`,
  maxHeight: `${MAX_HEIGHT}px`,
  padding: '12px 48px 12px 24px',
  border: 'none',
  outline: 'none',
  boxShadow: 'none',
  borderRadius: '24px',
  backgroundColor: 'transparent',
  resize: 'none',
  overflowY: 'auto',
  font: 'inherit',
  color: 'inherit',
  boxSizing: 'border-box',
};

const textareaSx = {
  border: 'none',
  outline: 'none',
  boxShadow: 'none',
  _focus: { border: 'none', outline: 'none', boxShadow: 'none' },
  _focusVisible: { border: 'none', outline: 'none', boxShadow: 'none' },
  _hover: { border: 'none', outline: 'none', boxShadow: 'none' },
  _active: { border: 'none', outline: 'none', boxShadow: 'none' },
};

const submitButtonWrapperStyle = {
  position: 'absolute',
  right: '8px',
  top: '6px',
  display: 'flex',
  justifyContent: 'flex-end',
  padding: '0 0 12px',
};

interface ChatInputProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  placeholder?: string;
  sx?: SystemStyleObject;
}

const ChatInput = ({
  value,
  onChange,
  onSubmit,
  placeholder = 'Search projects, analyze trends, or ask a question...',
  sx,
}: ChatInputProps) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const chatMenuRef = useRef<HTMLDivElement>(null);

  const adjustHeight = () => {
    const el = textareaRef.current;
    const chatMenuEl = chatMenuRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const newHeight = Math.min(el.scrollHeight, MAX_HEIGHT);
    el.style.height = `${newHeight}px`;
    if (!chatMenuEl) {
      return;
    }
    if (newHeight > MIN_HEIGHT) {
      chatMenuEl.style.position = 'relative';
      el.style.paddingRight = `24px`;
    } else {
      chatMenuEl.style.position = 'absolute';
      el.style.paddingRight = `48px`;
    }
  };

  useEffect(() => {
    adjustHeight();
  }, [value]);

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    onChange(e.target.value);
    adjustHeight();
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    onSubmit();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSubmit();
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ width: '100%' }}>
      <Box sx={{ ...wrapperStyle, ...sx }}>
        <Textarea
          ref={textareaRef}
          style={textareaStyle}
          sx={textareaSx}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          aria-label={placeholder}
          rows={1}
        />
        <Box sx={submitButtonWrapperStyle} ref={chatMenuRef}>
          <Button
            type="submit"
            variant="primary"
            sx={{ borderRadius: '16px' }}
            aria-label="Submit"
          >
            <Icon name={Icon.Name.ArrowRight} fill={Colors.White} size={16} />
          </Button>
        </Box>
      </Box>
    </form>
  );
};

export default ChatInput;
